import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { useAuth } from "./AuthProvider";
import { Spinner, Cluster, Stack, Button } from "@salesapp/ui";
import "./shell.css";

/** Gate for any route that needs an authenticated session. */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { status, sessionEndedReason, viewLocked, retrySession } = useAuth();
  const location = useLocation();

  if (status === "unavailable") {
    return <Stack gap={4} align="center" className="protected-route-loading">
      <p role="alert">We could not connect to your workspace. Please try again.</p>
      <Button onClick={retrySession}>Try again</Button>
    </Stack>;
  }

  if (status === "loading" || viewLocked) {
    return (
      <Cluster gap={4} justify="center" className="protected-route-loading">
        <Spinner size={28} />
      </Cluster>
    );
  }

  if (status === "anonymous") {
    const params = new URLSearchParams();
    params.set("returnTo", `${location.pathname}${location.search}${location.hash}`);
    if (sessionEndedReason === "expired") params.set("reason", "expired");
    if (sessionEndedReason === "replaced") params.set("reason", "replaced");
    if (sessionEndedReason === "idle") params.set("reason", "idle");
    return <Navigate to={`/login?${params.toString()}`} replace />;
  }

  return <>{children}</>;
}
